import { Form, Formik, type FormikHelpers } from 'formik';
import { useUpdateHouseMutation } from 'gcs-common/clients/api/entities/houses/houses-api';
import { PROCESS_DATA_SOURCE } from 'gcs-common/constants/processDataSource';
import { getSelectedHouseDetails } from 'gcs-common/slices/administration/houses/housesSelectors';
import { getHasPermission } from 'gcs-common/slices/currentUser/currentUserSelectors';
import { type MouseEvent, useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';
import CheckIcon from '../../../../img/check_green.svg?react';
import CancelIcon from '../../../../img/close_red.svg?react';
import EditIcon from '../../../../img/edit.svg?react';
import FormikSelect from '../../../FormikSelect/FormikSelect';
import styles from './styles.module.scss';

const validationSchema = Yup.object().shape({
  processDataSource: Yup.string().nullable().required('Erforderlich'),
});

const HouseDataSource = () => {
  const [isEditing, setIsEditing] = useState(false);

  const selectedHouse = useSelector(getSelectedHouseDetails);
  const isGlobalAdmin = useSelector(getHasPermission('global_admin'));

  const updateHouseMutation = useUpdateHouseMutation();

  const startEditing = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setIsEditing(true);
  }, []);

  const { processDataSource } = selectedHouse || {};

  const initialValues = useMemo(() => {
    return {
      processDataSource: processDataSource || '',
    };
  }, [processDataSource]);

  const cancelEditing = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setIsEditing(false);
  }, []);

  const submit = useCallback(
    async (values: typeof initialValues, { setSubmitting }: FormikHelpers<typeof initialValues>) => {
      try {
        await updateHouseMutation.mutateAsync(
          {
            path: {
              house_id: selectedHouse.id,
            },
            body: values,
          },
          {
            onSuccess: () => {
              setIsEditing(false);
            },
            onSettled: () => {
              setSubmitting(false);
            },
          },
        );
      } catch {
        // Error toast is handled by the mutation onError callback.
      }
    },
    [selectedHouse, updateHouseMutation],
  );

  return (
    <div className={styles.row}>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        enableReinitialize
        onSubmit={submit}
        validateOnChange
      >
        {({ isSubmitting, resetForm }) => (
          <Form noValidate className={styles.fullwidth}>
            {isGlobalAdmin && (
              <div className={styles.bottomHeader}>
                <div className={styles.buttonsWrapper}>
                  {isEditing ? (
                    <>
                      <button className={styles.saveButton} type="submit" disabled={isSubmitting}>
                        <CheckIcon />
                      </button>
                      <button
                        className={styles.saveButton}
                        type="button"
                        onClick={(event) => {
                          resetForm();
                          cancelEditing(event);
                        }}
                        disabled={isSubmitting}
                      >
                        <CancelIcon />
                      </button>
                    </>
                  ) : (
                    <button className={styles.saveButton} type="button" onClick={startEditing}>
                      <EditIcon />
                    </button>
                  )}
                </div>
              </div>
            )}
            <div className={styles.gaprow}>
              <FormikSelect
                label="Datenquelle für Vorgänge"
                options={PROCESS_DATA_SOURCE}
                labelKey="value"
                valueKey="key"
                disabled={!isEditing && !isGlobalAdmin}
                name="processDataSource"
              />
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default HouseDataSource;
